import uuid from 'uuid/v4';
import { cloneDeep } from 'lodash';
import { CONFIGURATION_TYPE } from '@/constants';
import { uploadFile, postOfferQuote } from '@/api';
import { getSteps, getStepById } from './steps';
import { CONFIGURATOR_MODE } from './constants';

export default {
  async open({ commit, dispatch, rootState }, { type }) {
    if (!type) {
      throw new Error('You must provider configurator type!');
    }

    if (rootState.configurationType !== type) {
      commit('clearConfigurations', null, { root: true });
    }

    commit('reset');
    commit('setType', type);
    commit('setMode', CONFIGURATOR_MODE.CREATE);

    if (type === CONFIGURATION_TYPE.FULL) {
      commit('setFiles', []);
    }

    dispatch('initializeStepsState', { type, defaultValid: false });
    commit('setConfigurationType', type, { root: true });
    dispatch('resolveInitialStep');
    commit('setOpened', true);
  },
  async edit({ commit, dispatch, rootState }, { type, configurationId }) {
    const { configurations = [] } = rootState;

    const configuration = configurations.find(x => x.id === configurationId);

    if (!configuration) {
      throw new Error(
        `Could not find configuration with id ${configurationId}`,
      );
    }

    commit('reset');
    commit('setType', type);
    commit('setMode', CONFIGURATOR_MODE.EDIT);

    if (type === CONFIGURATION_TYPE.FULL) {
      commit('setFiles', []);
    }

    dispatch('initializeStepsState', { type, defaultValid: true });
    commit('setForm', JSON.parse(JSON.stringify(configuration)));
    dispatch('resolveInitialStep');
    commit('setOpened', true);
  },
  async save({ state, getters, commit }) {
    const { form } = state;
    const { editing } = getters;

    if (!form.id || !editing) {
      throw new Error('Cannot save. You are not in edit mode!');
    }

    const configuration = JSON.parse(JSON.stringify(form));

    commit(
      'updateConfiguration',
      { id: form.id, configuration },
      { root: true },
    );
  },
  initializeStepsState({ commit }, { type, defaultValid }) {
    const steps = getSteps(type);
    const stepsState = {};

    steps.forEach((step, stepId) => {
      const stepState = {
        valid$: defaultValid,
        initialized$: false,
        loading$: false,
      };

      if (typeof step.defaultState === 'function') {
        Object.assign(stepState, step.defaultState());
      }

      stepsState[stepId] = stepState;
    });

    commit('setStepsIds', Array.from(steps.keys()));
    commit('setStepsState', stepsState);
  },
  async resolveInitialStep({ state, dispatch }) {
    const { files } = state;
    const steps = getSteps(state.type);

    if (steps.size <= 0) {
      throw new Error('Steps map is empty!');
    }

    let initialStepId = steps.keys().next().value;

    if (files.length > 0) {
      initialStepId = 'style';
    }

    await dispatch('setCurrentStep', { stepId: initialStepId });
  },
  async setCurrentStep({ commit, dispatch }, { stepId }) {
    commit('setCurrentStepId', stepId);
    await dispatch('initializeStep', { stepId });
    await dispatch('validateStep', { stepId });
  },
  async initializeStep({ state, commit, getters }, { stepId }) {
    const { getStepState } = getters;
    const step = getStepById(stepId);
    const stepState = Object.assign({}, getStepState(stepId));

    try {
      Object.assign(stepState, { initialized$: false, loading$: true });
      commit('updateStepState', { stepId, stepState });

      if (typeof step.init === 'function') {
        await step.init({
          state: stepState,
          form: state.form,
        });
      }

      commit('updateStepState', { stepId, stepState });
    } finally {
      Object.assign(stepState, { initialized$: true, loading$: false });
    }

    return stepState;
  },
  updateFormField({ state, commit, dispatch }, { field, value }) {
    if (!field) {
      throw new Error('Field name cannot be empty.');
    }

    const { currentStep } = state;

    const oldForm = cloneDeep(state.form);
    const newForm = Object.assign({}, state.form, {
      [field]: value,
    });

    commit('setForm', newForm);
    dispatch('validateStep', { stepId: currentStep.id });
    dispatch('notifyFormChange', { form: newForm, oldForm });
  },
  async notifyFormChange({ state, dispatch }) {
    const { currentStep, type } = state;

    if (!currentStep.id) {
      return;
    }

    const steps = getSteps(type);
    const stepsIds = Array.from(steps.keys());
    const index = stepsIds.indexOf(currentStep.id);

    if (index === -1) {
      return;
    }

    for (let i = index + 1; i < stepsIds.length; i += 1) {
      const stepId = stepsIds[i];

      await dispatch('initializeStep', { stepId });
      await dispatch('validateStep', { stepId });
    }
  },
  async validateStep({ state, commit, getters }, { stepId }) {
    const { form, files } = state;
    const { getStepState } = getters;

    const step = getStepById(stepId);
    let stepState = getStepState(stepId);
    let isValid = false;

    if (typeof step.validate === 'function') {
      isValid = await step.validate({ state: stepState, form, files });
    }

    stepState = Object.assign({}, stepState, { valid$: isValid });
    commit('updateStepState', { stepId, stepState });

    return isValid;
  },
  async goToNextStep({ state, dispatch }) {
    const { currentStep, type } = state;

    const steps = getSteps(type);
    const iterator = steps.keys();
    let nextStepId = null;

    for (const stepId of iterator) {
      if (stepId === currentStep.id) {
        nextStepId = iterator.next().value;
        break;
      }
    }

    const valid = await dispatch('validateStep', { stepId: currentStep.id });

    if (!valid) {
      return;
    }

    if (nextStepId) {
      dispatch('setCurrentStep', { stepId: nextStepId });
    } else {
      dispatch('handleCompletion');
    }
  },
  goToPreviousStep({ state, dispatch }) {
    const { currentStep, type } = state;

    const steps = getSteps(type);
    const stepsReversed = new Map(Array.from(steps).reverse());
    const iterator = stepsReversed.keys();
    let prevStepId = null;

    for (const stepId of iterator) {
      if (stepId === currentStep.id) {
        prevStepId = iterator.next().value;
        break;
      }
    }

    if (!prevStepId) {
      return;
    }

    dispatch('setCurrentStep', { stepId: prevStepId });
  },
  goToStep({ state, getters, dispatch }, { stepId }) {
    if (!stepId) {
      throw new Error('Step id is required!');
    }

    const { currentStep, type } = state;
    const { creating } = getters;

    if (currentStep.id === stepId) {
      return;
    }

    const steps = getSteps(type);
    const stepsIds = Array.from(steps.keys());
    const currentStepIndex = stepsIds.indexOf(currentStep.id);
    const targetStepIndex = stepsIds.indexOf(stepId);

    if (creating && targetStepIndex > currentStepIndex) {
      return;
    }

    dispatch('setCurrentStep', { stepId });
  },
  handleCompletion({ state, commit, getters }) {
    const { form } = state;
    const { editing, creating } = getters;

    const configuration = JSON.parse(JSON.stringify(form));

    if (creating) {
      commit('addConfiguration', configuration, { root: true });
    } else if (editing) {
      commit(
        'updateConfiguration',
        { id: form.id, configuration },
        { root: true },
      );
    } else {
      throw new Error('Unknown configurator mode.');
    }

    commit('setCurrentStepId', 'summary');
  },
  async addFile({ state, commit, dispatch }, file) {
    const { currentStep } = state;

    const fileItem = {
      id: uuid(),
      name: file.name,
      size: file.size,
      format: file.name.split('.').pop(),
      uploaded: false,
      uploading: false,
      progress: 0,
      error: null,
      fileEntityId: null,
    };

    commit('setFiles', [...state.files, fileItem]);

    try {
      commit('updateFile', Object.assign({}, fileItem, { uploading: true }));

      const result = await uploadFile(file, progressPercentage => {
        commit(
          'updateFile',
          Object.assign({}, fileItem, {
            uploading: true,
            progress: progressPercentage,
          }),
        );
      });

      commit(
        'updateFile',
        Object.assign({}, fileItem, {
          fileEntityId: result.fileEntityId,
          uploading: false,
          progress: 100,
          uploaded: result.uploaded,
        }),
      );

      dispatch('validateStep', { stepId: currentStep.id });
    } catch (error) {
      commit(
        'updateFile',
        Object.assign({}, fileItem, {
          uploaded: false,
          uploading: false,
          error: 'Failed to upload file',
        }),
      );
    }
  },
  async sendOfferQuote({ state, rootState }, { client }) {
    const { type, files } = state;
    const { configurations } = rootState;
    delete client.agreement;

    if (type === CONFIGURATION_TYPE.PROJECT && files.length === 0) {
      throw new Error('Files are required.');
    }

    const formData = { client };

    formData.items = configurations.map(item => {
      const newItem = {
        pid: item.profile.id,
        sfid: item.safety.id,
        cid_i: item.colorInner.id,
        cid_o: item.colorOuter.id,
        name: item.name,
      };

      if (type === CONFIGURATION_TYPE.FULL) {
        Object.assign(newItem, {
          wid: item.windowOpeningType.id,
          width: item.width,
          height: item.height,
          count: item.count,
        });
      }

      if (item.innovations.length > 0) {
        newItem.inn = item.innovations.map(x => x.id);
      }

      return newItem;
    });

    if (type === CONFIGURATION_TYPE.PROJECT) {
      formData.files = files.map(file => file.fileEntityId);
    }

    await postOfferQuote(formData);
  },
  close({ commit }) {
    commit('setOpened', false);
    commit('reset');
  },
};
